import { useSelector } from "react-redux";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { useTheme } from "@fluentui/react/lib/Theme";

import { IWithTranslatorProps, WithTranslator } from "Components/Generic/Translator";
import { selectIsFiltering, selectIsImporting, selectIsSorting } from "Store/Selectors/UI";
import { joinNonEmpty } from "Utils/String";
import { useEffect, useState } from "react";
import { useBackgroundActivityStyles } from "./UseBackgroundActivityStyles";

export const BackgroundActivity = WithTranslator(({ translator }: IWithTranslatorProps): JSX.Element => {
  const styles = useBackgroundActivityStyles();
  const { spacing } = useTheme();
  const isSorting = useSelector(selectIsSorting);
  const isImporting = useSelector(selectIsImporting);
  const isFiltering = useSelector(selectIsFiltering);

  const sortTranslation = translator.get(`sort.inprogress`, `Sorting projects`);
  const importTranslation = translator.get(`import.inprogress`, `Importing projects`);
  const filterTranslation = translator.get(`filter.inprogress`, `Filtering projects`);

  const [sortText, setSortText] = useState(``);
  const [importText, setImportText] = useState(``);
  const [filterText, setFilterText] = useState(``);
  const [active, setActive] = useState(isSorting || isImporting || isFiltering);

  useEffect(() => {
    if (!active) {
      if (isSorting) {
        setSortText(sortTranslation);
      }
      if (isImporting) {
        setImportText(importTranslation);
      }
      if (isFiltering) {
        setFilterText(filterTranslation);
      }
      setActive(isSorting || isImporting || isFiltering);
      return () => undefined;
    }

    const t = setTimeout(() => {
      if (!isSorting) {
        setSortText(``);
      }
      if (!isImporting) {
        setImportText(``);
      }
      if (!isFiltering) {
        setFilterText(``);
      }
      setActive(isSorting || isImporting || isFiltering);
    }, 250);

    return () => {
      clearTimeout(t);
    };
  }, [isSorting, isImporting, isFiltering, active, sortTranslation, importTranslation, filterTranslation]);

  return (
    <Stack
      className={styles.root}
      horizontal
      verticalAlign="center"
      horizontalAlign="start"
      tokens={{ childrenGap: spacing.s1 }}
    >
      <Stack.Item>{active && <Spinner size={SpinnerSize.small} />}</Stack.Item>
      <Stack.Item>
        <Text variant="small">{active && `${joinNonEmpty([sortText, filterText, importText], `, `)}...`}</Text>
      </Stack.Item>
    </Stack>
  );
});
